import { CircularProgress } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";

import { ArrowDownIcon, ArrowUpIcon } from "../../../assets/img/icons";
import Tooltip from "../../../components/Tooltip";
import TopSection from "../../../components/TopSection";

import { getTradingSessionsStats } from "../../../services/api/tradingSessions";
import { TradingStyleValue } from "../../../types/enums/TradingStyle";
import { getFormattedDateInput } from "../../../utils/date";
import styles from "./styles.module.scss";

const formatNumber = (
    value: number,
    options: { decimals?: number } = {}
): string => {
    const { decimals = 0 } = options;
    return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
};

const formatApiValue = (value: number) =>
    new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 10,
    }).format(value);

type PerformanceBaseSource = "demo" | "funding";

interface Props {
    userId: string;
    startDate: Date;
    endDate: Date;
    timeslotFilter?: string;
    challengeFilter?: string;
    tradingStyleFilter?: TradingStyleValue;
    timeslotCategories: Record<string, string[]>;
    /** Incrémenter pour forcer un nouveau fetch des stats (ex. après création de session) */
    refreshTrigger?: number;
}

const TradesTopSections: FC<Props> = ({
    userId,
    startDate,
    endDate,
    timeslotFilter,
    challengeFilter,
    tradingStyleFilter,
    timeslotCategories,
    refreshTrigger = 0,
}) => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

    const [isLoading, setIsLoading] = useState(true);
    const [pnlShowDollarPrimary, setPnlShowDollarPrimary] = useState(true);
    const [baseSourceOverride, setBaseSourceOverride] = useState<
        PerformanceBaseSource | null
    >(null);
    const [topSection, setTopSection] = useState({
        dayWin: 0,
        sessionWin: 0,
        tradeWin: 0,
        profitFactor: null as number | null,
        gainLoss: 0,
        gainLossPercentage: 0,
        averageGain: 0,
        averageLoss: 0,
        methodRespected: 0,
        screenTime: "00h00",
        performanceBase: undefined as number | undefined,
        performanceBaseSource: undefined as PerformanceBaseSource | undefined,
        demoAccountBase: undefined as number | undefined,
        fundingBase: null as number | null,
    });

    const isMobile = useMediaQuery("(max-width: 1400px)");

    const togglePnlDisplay = () => setPnlShowDollarPrimary((prev) => !prev);

    useEffect(() => {
        const fetchTopSection = async () => {
            setIsLoading(true);
            try {
                let timeslots: string[] | undefined;
                if (timeslotFilter) {
                    timeslots =
                        timeslotCategories[
                            timeslotFilter as keyof typeof timeslotCategories
                        ];
                }

                let isChallenge: boolean | undefined;
                if (challengeFilter === "only") {
                    isChallenge = true;
                } else if (challengeFilter === "exclude") {
                    isChallenge = false;
                }

                const data = await getTradingSessionsStats({
                    userId,
                    start: getFormattedDateInput(startDate),
                    end: getFormattedDateInput(endDate),
                    timeslots,
                    isChallenge,
                    tradingStyle: tradingStyleFilter,
                });

                if (!data) {
                    throw new Error("No data found");
                }

                setBaseSourceOverride(null);
                setTopSection({
                    dayWin: data.dayWin,
                    sessionWin: data.sessionWin ?? 0,
                    tradeWin: data.tradeWin ?? 0,
                    profitFactor:
                        data.profitFactor == null
                            ? null
                            : Number(data.profitFactor),
                    gainLoss: data.gainLoss,
                    gainLossPercentage: data.gainLossPercentage,
                    averageGain: data.averageGain,
                    averageLoss: data.averageLoss,
                    methodRespected: data.methodRespected,
                    screenTime: data.screenTime,
                    performanceBase: data.performanceBase,
                    performanceBaseSource: data.performanceBaseSource,
                    demoAccountBase: data.demoAccountBase,
                    fundingBase:
                        data.fundingBase != null &&
                        Number.isFinite(Number(data.fundingBase)) &&
                        Number(data.fundingBase) > 0
                            ? Number(data.fundingBase)
                            : null,
                });
            } catch (error) {
                console.error("Error fetching top section data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTopSection();
    }, [
        startDate,
        endDate,
        userId,
        timeslotFilter,
        challengeFilter,
        tradingStyleFilter,
        timeslotCategories,
        refreshTrigger,
    ]);

    const canToggleBase =
        topSection.fundingBase != null &&
        topSection.demoAccountBase != null &&
        topSection.fundingBase > 0 &&
        topSection.demoAccountBase > 0;

    const activeBaseSource: PerformanceBaseSource =
        baseSourceOverride ??
        topSection.performanceBaseSource ??
        "demo";

    const activeBase =
        activeBaseSource === "funding" && topSection.fundingBase != null
            ? topSection.fundingBase
            : topSection.demoAccountBase ?? topSection.performanceBase;

    const displayGainLossPercentage = useMemo(() => {
        if (activeBase == null || activeBase <= 0) {
            return topSection.gainLossPercentage;
        }
        return parseFloat(((topSection.gainLoss / activeBase) * 100).toFixed(2));
    }, [activeBase, topSection.gainLoss, topSection.gainLossPercentage]);

    const performanceBaseAmountLabel =
        activeBase != null ? `Base ${formatNumber(activeBase)} $` : null;

    const setBaseSource = (source: PerformanceBaseSource) => {
        if (!canToggleBase) return;
        setBaseSourceOverride(source);
    };

    const pnlColor =
        topSection.gainLoss > 0
            ? "#00B69C"
            : topSection.gainLoss < 0
            ? "var(--danger)"
            : "var(--text-primary)";
    const pnlPercentageColor =
        displayGainLossPercentage > 0
            ? "#00B69C"
            : displayGainLossPercentage < 0
            ? "var(--danger)"
            : "var(--text-primary)";
    const averageGainColor =
        topSection.averageGain > 0 ? "#00B69C" : "var(--text-primary)";
    const averageLossColor =
        topSection.averageLoss < 0 ? "var(--danger)" : "var(--text-primary)";
    const dayWinColor =
        topSection.dayWin > 0 ? "#00B69C" : "var(--text-primary)";
    const sessionWinColor =
        topSection.sessionWin > 0 ? "#00B69C" : "var(--text-primary)";
    const tradeWinColor =
        topSection.tradeWin > 0 ? "#00B69C" : "var(--text-primary)";

    const baseControls = performanceBaseAmountLabel ? (
        <div className={styles.pnlBaseControls}>
            <span
                className={styles.pnlBaseLabel}
                data-testid="performance-base"
            >
                {performanceBaseAmountLabel}
            </span>
            {canToggleBase ? (
                <div
                    className={styles.pnlBaseSwitch}
                    role="group"
                    aria-label="Base de calcul PnL"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        className={`${styles.pnlBaseSwitchOption} ${
                            activeBaseSource === "demo"
                                ? styles.pnlBaseSwitchOptionActive
                                : ""
                        }`}
                        aria-pressed={activeBaseSource === "demo"}
                        onClick={() => setBaseSource("demo")}
                    >
                        Démo
                    </button>
                    <button
                        type="button"
                        className={`${styles.pnlBaseSwitchOption} ${
                            activeBaseSource === "funding"
                                ? styles.pnlBaseSwitchOptionActive
                                : ""
                        }`}
                        aria-pressed={activeBaseSource === "funding"}
                        onClick={() => setBaseSource("funding")}
                    >
                        Financé
                    </button>
                </div>
            ) : (
                <span className={styles.pnlBaseModeBadge}>
                    {activeBaseSource === "funding" ? "Financé" : "Démo"}
                </span>
            )}
        </div>
    ) : null;

    const dayWinCard = (
        <div className={`top_section ${styles.dayWinCard}`}>
            <span className="top_section_title">Winrate</span>
            {isLoading ? (
                <div className="top_section_value">
                    <CircularProgress size={24} />
                </div>
            ) : (
                <div className={styles.dayWinValuesRow}>
                    <div className={styles.dayWinSubCell}>
                        <span className={styles.dayWinSubLabel}>Par jour</span>
                        <span
                            className={styles.dayWinSubValue}
                            style={{ color: dayWinColor }}
                        >
                            {formatNumber(topSection.dayWin)}%
                        </span>
                    </div>
                    <div className={styles.dayWinSeparator} aria-hidden />
                    <div className={styles.dayWinSubCell}>
                        <span className={styles.dayWinSubLabel}>
                            Par sessions
                        </span>
                        <span
                            className={styles.dayWinSubValue}
                            style={{ color: sessionWinColor }}
                        >
                            {formatNumber(topSection.sessionWin)}%
                        </span>
                    </div>
                    <div className={styles.dayWinSeparator} aria-hidden />
                    <div className={styles.dayWinSubCell}>
                        <span className={styles.dayWinSubLabel}>Par trade</span>
                        <span
                            className={styles.dayWinSubValue}
                            style={{ color: tradeWinColor }}
                        >
                            {formatNumber(topSection.tradeWin)}%
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    const profitFactorColor =
        topSection.profitFactor == null
            ? "var(--text-primary)"
            : topSection.profitFactor > 1
            ? "#00B69C"
            : topSection.profitFactor < 1
            ? "var(--danger)"
            : "var(--text-primary)";

    const profitFactorCard = (
        <div className={`top_section ${styles.profitFactorCard}`}>
            <span className={`top_section_title ${styles.profitFactorTitle}`}>
                Profit factor
                <Tooltip
                    placement="bottom"
                    content="Somme des gains divisée par la somme des pertes, sur la période affichée. Au-dessus de 1, les gains dépassent les pertes. Le tiret « — » s'affiche lorsqu'il n'y a aucune perte."
                >
                    <button
                        type="button"
                        className={styles.metricHelp}
                        aria-label="Définition du profit factor"
                    >
                        ?
                    </button>
                </Tooltip>
                <span className={styles.metricFormula}>(gains / pertes)</span>
            </span>
            {isLoading ? (
                <div className="top_section_value">
                    <CircularProgress size={24} />
                </div>
            ) : (
                <div
                    className="top_section_value"
                    style={{ color: profitFactorColor }}
                >
                    {topSection.profitFactor == null
                        ? "—"
                        : formatNumber(topSection.profitFactor, {
                              decimals: 2,
                          })}
                </div>
            )}
        </div>
    );

    const averagePerformanceCard = (
        <div className={`top_section ${styles.avgPerformanceCard}`}>
            <span className="top_section_title">
                P&L Moyen
                <span className={styles.metricFormula}> (par trade)</span>
            </span>
            {isLoading ? (
                <div className="top_section_value">
                    <CircularProgress size={24} />
                </div>
            ) : (
                <div className={styles.avgPerformanceValuesRow}>
                    <div className={styles.avgPerformanceSubCell}>
                        <span className={styles.avgPerformanceSubLabel}>
                            Gain moyen
                        </span>
                        <span
                            className={styles.avgPerformanceSubValue}
                            style={{ color: averageGainColor }}
                        >
                            {formatApiValue(topSection.averageGain)}$
                        </span>
                    </div>
                    <div className={styles.avgPerformanceSubCell}>
                        <span className={styles.avgPerformanceSubLabel}>
                            Perte moyenne
                        </span>
                        <span
                            className={styles.avgPerformanceSubValue}
                            style={{ color: averageLossColor }}
                        >
                            {formatApiValue(topSection.averageLoss)}$
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    const pnlValues = (
        <div className={styles.pnlCardValueRow}>
            {pnlShowDollarPrimary ? (
                <>
                    <div
                        className="top_section_value"
                        style={{ color: pnlColor }}
                    >
                        {formatNumber(topSection.gainLoss)}$
                    </div>
                    <div
                        className={styles.pnlPercentage}
                        style={{ color: pnlPercentageColor }}
                    >
                        {displayGainLossPercentage >= 0 ? (
                            <ArrowUpIcon
                                className={styles.pnlPercentageArrow}
                            />
                        ) : (
                            <ArrowDownIcon
                                className={styles.pnlPercentageArrow}
                            />
                        )}
                        <span>
                            {formatNumber(displayGainLossPercentage, {
                                decimals: 1,
                            })}
                            %
                        </span>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="top_section_value"
                        style={{ color: pnlPercentageColor }}
                    >
                        {formatNumber(displayGainLossPercentage, {
                            decimals: 1,
                        })}
                        %
                    </div>
                    <div
                        className={styles.pnlPercentage}
                        style={{ color: pnlColor }}
                    >
                        {topSection.gainLoss >= 0 ? (
                            <ArrowUpIcon
                                className={styles.pnlPercentageArrow}
                            />
                        ) : (
                            <ArrowDownIcon
                                className={styles.pnlPercentageArrow}
                            />
                        )}
                        <span>{formatNumber(topSection.gainLoss)}$</span>
                    </div>
                </>
            )}
        </div>
    );

    const sections = isMobile ? (
        <div className={styles.dashboard_top_sections_mobile}>
            <div className={styles.double}>
                <div
                    className={`top_section ${styles.pnlCard}`}
                    onClick={togglePnlDisplay}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            togglePnlDisplay();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Inverser l'affichage P&L ($ / %)"
                >
                    <div className={styles.pnlCardTitleRow}>
                        <span className="top_section_title">Net P&L</span>
                        <span className={styles.pnlCardUnit}>
                            {pnlShowDollarPrimary
                                ? "valeurs en $"
                                : "valeurs en %"}
                        </span>
                        {baseControls}
                    </div>
                    {isLoading ? (
                        <div className="top_section_value">
                            <CircularProgress size={24} />
                        </div>
                    ) : (
                        pnlValues
                    )}
                </div>
                {dayWinCard}
            </div>
            <div className={styles.double}>
                {profitFactorCard}
                {averagePerformanceCard}
            </div>
            <div className={styles.double}>
                <TopSection
                    loading={isLoading}
                    title={isMobile ? "Méthode" : "Méthode respectée"}
                    value={`${formatNumber(topSection.methodRespected)}%`}
                />
                <TopSection
                    loading={isLoading}
                    title="Temps d'écran"
                    value={`${topSection.screenTime}`}
                />
            </div>
        </div>
    ) : (
        <div className={styles.dashboard_top_sections}>
            <div
                className={`top_section ${styles.pnlCard}`}
                onClick={togglePnlDisplay}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        togglePnlDisplay();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="Inverser l'affichage P&L ($ / %)"
            >
                <div className={styles.pnlCardTitleRow}>
                    <span className="top_section_title">Net P&L</span>
                    <span className={styles.pnlCardUnit}>
                        {pnlShowDollarPrimary ? "valeurs en $" : "valeurs en %"}
                    </span>
                    {baseControls}
                </div>
                {isLoading ? (
                    <div className="top_section_value">
                        <CircularProgress size={24} />
                    </div>
                ) : (
                    pnlValues
                )}
            </div>
            {dayWinCard}
            {profitFactorCard}
            <TopSection
                loading={isLoading}
                title="Méthode respectée"
                value={`${formatNumber(topSection.methodRespected)}%`}
            />
            <TopSection
                loading={isLoading}
                title="Temps d'écran"
                value={`${topSection.screenTime}`}
            />
            {averagePerformanceCard}
        </div>
    );

    return <div className={styles.topSectionsContainer}>{sections}</div>;
};

export default TradesTopSections;
